import { ReactNode } from 'react';

import { Regulations } from '@/api/fishrules/getSpeciesRegulationsAtLocation';
import { Boundary } from '@/api/fishrules/getBoundariesAtLocation';
import { IAccordionData } from '../../Accordion/Accordion';
import { getRestrictionsAndRequirements } from '../../ProhibitedMethods/helpers';
import { Text } from '../../Text/Text';
import { TextFromHTML } from '../../TextFromHTML/TextFromHTML';
import { convertHtmlToReactElement } from '../../TextFromHTML/helpers';

export const accordionTitle = (label: ReactNode, value?: ReactNode) => (
  <span className="flex min-w-0 flex-1 items-center gap-2 text-left">
    <Text as="span" variant="sm" className="text-trench mb-0 min-w-0 flex-1 font-semibold">
      {label}
    </Text>
    {value !== undefined && (
      <Text as="span" variant="sm" className="text-trench mb-0 max-w-[60%] truncate font-semibold">
        {value}
      </Text>
    )}
  </span>
);

interface IOptions {
  boundaries?: Boundary[];
  // Skip "harvest rules" entries (see (Harvest rules) annotations below).
  // Used when a species is prohibited — the rules governing how to harvest
  // are meaningless if you cannot harvest at all, so we hide them while
  // keeping the additional-info entries.
  skipHarvestRules?: boolean;
}

// Each entry below is annotated with its group:
//   (Harvest rules)   — rules that govern how the species can be legally
//                       harvested (limits, sizes, gear). Hidden when the
//                       species is prohibited.
//   (Additional info) — descriptive / contextual info about the species or
//                       location (licenses, notes, edibility, synonyms,
//                       boundary notes). Always shown.
export const buildAccordionData = (
  r: Regulations,
  { boundaries, skipHarvestRules = false }: IOptions = {},
): IAccordionData[] => {
  const data: IAccordionData[] = [];

  if (!skipHarvestRules) {
    pushHarvestRules(data, r);
  }

  pushAdditionalInfo(data, r, boundaries);

  return data;
};

const pushHarvestRules = (data: IAccordionData[], r: Regulations): void => {
  // (Harvest rules) Bag limit
  if (r.bag_limit !== null) {
    data.push({
      id: 'bag-limit',
      title: accordionTitle('Bag limit', r.bag_limit.toString()),
      body: (
        <Text variant="sm" className="mb-0">
          The maximum number of individual fish legally harvested per harvester per day. Bag limits
          are only for properly licensed anglers actively harvesting the species. People who are not
          actively harvesting or are not properly licensed (if a license is required) may NOT be
          counted for the purpose of bag limits.
        </Text>
      ),
    });
  }

  // (Harvest rules) Min size
  if (r.min_size !== null) {
    data.push({
      id: 'min-size',
      title: accordionTitle(
        'Min size',
        `${r.min_size}${r.measurement_unit_symbol}${r.measurement_name ? ` (${r.measurement_name})` : ''}`,
      ),
      body: (
        <>
          <Text variant="sm" className={r.measurement_name ? 'mb-2' : 'mb-0'}>
            No person shall harvest any individual fish less than the minimum size limit.
          </Text>
          {r.measurement_name && (
            <Text variant="sm" className="mb-0">
              {r.measurement_description}
            </Text>
          )}
        </>
      ),
    });
  }

  // (Harvest rules) Max size
  if (r.max_size !== null) {
    data.push({
      id: 'max-size',
      title: accordionTitle(
        'Max size',
        `${r.max_size}${r.measurement_unit_symbol}${r.measurement_name ? ` (${r.measurement_name})` : ''}`,
      ),
      body: (
        <>
          <Text variant="sm" className={r.measurement_name ? 'mb-2' : 'mb-0'}>
            No person shall harvest any individual fish greater than the maximum size except as
            permitted.
          </Text>
          {r.measurement_name && (
            <Text variant="sm" className="mb-0">
              {r.measurement_description}
            </Text>
          )}
        </>
      ),
    });
  }

  // (Harvest rules) Aggregate limit
  if (r.aggregate_limit !== null) {
    data.push({
      id: 'aggregate-limit',
      title: accordionTitle('Aggregate limit', r.aggregate_limit.toString()),
      body: (
        <Text variant="sm" className="mb-0">
          The maximum number of individual fish across an aggregate group of species legally
          harvested per harvester per day.
        </Text>
      ),
    });
  }

  // (Harvest rules) Trophy / memorable limit
  if (r.trophy_limit !== null) {
    data.push({
      id: 'trophy-limit',
      title: accordionTitle(
        'Memorable / trophy limits',
        r.trophy_size !== null ? `${r.trophy_limit} > ${r.trophy_size}` : r.trophy_limit.toString(),
      ),
      body: (
        <Text variant="sm" className="mb-0">
          The maximum number of trophy individuals legally harvested per harvester per day. A trophy
          is any individual of a length greater than the max size limit. Trophy individuals are
          included in the daily bag limit unless stated otherwise.
        </Text>
      ),
    });
  }

  // (Harvest rules) Vessel limit
  if (r.vessel_limit !== null) {
    data.push({
      id: 'vessel-limit',
      title: accordionTitle('Vessel limit', r.vessel_limit.toString()),
      body: (
        <Text variant="sm" className="mb-0">
          The maximum number of individual fish legally possessed aboard any vessel.
        </Text>
      ),
    });
  }

  // (Harvest rules) Keep intact
  if (r.keep_intact === 1) {
    data.push({
      id: 'keep-intact',
      title: accordionTitle('Requirement', 'Keep intact'),
      body: (
        <Text variant="sm" className="mb-0">
          In waters where minimum-length or slot-size limits apply, game fish may not be filleted,
          nor their head or tail fin removed until the angler has completed fishing for the day.
        </Text>
      ),
    });
  }

  // (Harvest rules) Special gear
  if (r.gear_name) {
    data.push({
      id: 'gear',
      title: accordionTitle('Special gear'),
      body: (
        <>
          <Text variant="sm" className="mb-1">
            {r.gear_name}
          </Text>
          <Text variant="sm" className="mb-0">
            {r.gear_description}
          </Text>
        </>
      ),
    });
  }
};

const pushAdditionalInfo = (
  data: IAccordionData[],
  r: Regulations,
  boundaries: Boundary[] | undefined,
): void => {
  // (Additional info) Restrictions & requirements
  const restrictionsAndRequirements = getRestrictionsAndRequirements(r);
  if (restrictionsAndRequirements.length) {
    data.push({
      id: 'restrictions',
      title: accordionTitle('Restrictions & requirements'),
      body: (
        <>
          {restrictionsAndRequirements.map(({ id, label, description }) => (
            <div key={id} className="py-2">
              <Text variant="sm" className="mb-1">
                {label}
              </Text>
              <Text variant="sm" className="mb-0">
                {description}
              </Text>
            </div>
          ))}
        </>
      ),
    });
  }

  // (Additional info) Required licenses
  if (r.additional_licenses_required) {
    data.push({
      id: 'licenses',
      title: accordionTitle('Required licenses'),
      body: <TextFromHTML html={r.additional_licenses_required} variant="sm" />,
    });
  }

  // (Additional info) Additional information
  if (r.notes) {
    data.push({
      id: 'notes',
      title: accordionTitle('Additional information'),
      body: <div>{convertHtmlToReactElement(r.notes, ['a'])}</div>,
    });
  }

  // (Additional info) Edibility
  if (r.edibility) {
    data.push({
      id: 'edibility',
      title: accordionTitle('Edibility'),
      body: <TextFromHTML html={r.edibility} variant="sm" />,
    });
  }

  // (Additional info) Synonyms
  if (r.synonyms.length) {
    data.push({
      id: 'synonyms',
      title: accordionTitle('Synonyms'),
      body: (
        <Text as="span" variant="sm" className="mb-0">
          {r.synonyms.join(', ')}
        </Text>
      ),
    });
  }

  // (Additional info) Location regulation notes
  if (r.location_regulation_notes) {
    data.push({
      id: 'location-notes',
      title: accordionTitle('Location regulation notes'),
      body: <TextFromHTML html={r.location_regulation_notes} variant="sm" />,
    });
  }

  // (Additional info) Location specific information — comes from boundary-level
  // regulationNotes, not from the species' regulation row.
  const locationSpecificInformation = (boundaries ?? [])
    .map(b => b.regulationNotes)
    .filter(notes => notes);
  if (locationSpecificInformation.length) {
    data.push({
      id: 'location-specific-info',
      title: accordionTitle('Location specific information'),
      body: (
        <>
          {locationSpecificInformation.map(notes => (
            <TextFromHTML key={notes} html={notes} variant="sm" />
          ))}
        </>
      ),
    });
  }
};
